import Button from '@naturehouse/design-system/components/atoms/button/Button';
import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import ApiRequestState from '../../../common/search/store/ApiRequestState';
import type AbstractFilter from '../../../common/search/store/AbstractFilter';
import GTM from '../../../gtm/GTM';
import GTMOverviewFilterRemoveAllModel from '../../../gtm/models/GTMOverviewFilterRemoveAllModel';
import HouseSearchFilters from '../store/HouseSearchFilters';
import AggregationFilter from '../store/AggregationFilter';

export enum SearchFilterTypes {
    AGGREGATIONS = 'aggregations',
    ALL = 'all'
}

export default class ResetSearchFilterButton extends Button implements Observer {
    readonly #apiRequestState = ApiRequestState.getInstance();

    readonly #filters = HouseSearchFilters.getInstance();

    readonly #aggregationFilters = this.#filters.all.filter(
        (filter: AbstractFilter) => filter instanceof AggregationFilter
    ) as AggregationFilter[];

    get #removeSearchFilter(): SearchFilterTypes {
        const value = this.dataset.removeSearchFilter ?? null;
        const types = <string[]>Object.values(SearchFilterTypes);

        if (value === null || !types.includes(value)) {
            return SearchFilterTypes.AGGREGATIONS;
        }

        return value as SearchFilterTypes;
    }

    get #activeAggregations(): number {
        let activeAggregateFilterAmount = 0;
        this.#aggregationFilters.forEach((filter) => {
            const parameters = filter.parameters[filter.parameterName];
            if (!parameters) {
                return;
            }

            activeAggregateFilterAmount += parameters.split(',').length;
        });

        return activeAggregateFilterAmount;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.#apiRequestState.attach(this);
        this.#setHidden();

        this.addEventListener('click', this.#handleClick);
    }

    public disconnectedCallback(): void {
        this.removeEventListener('click', this.#handleClick);
    }

    public update(subject: Subject | CustomEvent): void {
        if (
            !(subject instanceof ApiRequestState) ||
            subject.state !== ApiRequestState.REQUEST_SUCCESS
        ) {
            return;
        }

        this.#setHidden();
    }

    #setHidden(): void {
        this.toggleAttribute('hidden', this.#activeAggregations === 0);
    }

    readonly #handleClick = (): void => {
        const searchFilter: string = this.#removeSearchFilter;
        if (searchFilter === SearchFilterTypes.AGGREGATIONS) {
            this.#aggregationFilters.forEach((filter) => {
                filter.clear();
            });
        }

        if (searchFilter === SearchFilterTypes.ALL) {
            this.#filters.clear();
        }

        this.#setHidden();
        GTM.track(
            GTMOverviewFilterRemoveAllModel.parseToGTMData({
                location: 'onder resultaten'
            })
        );
    };
}

if (!customElements.get('reset-search-filter-button')) {
    customElements.define('reset-search-filter-button', ResetSearchFilterButton, {
        extends: 'button'
    });
}
